/**
 * Feedbackcontrole — is de feedback op de vorige versie verwerkt in deze versie?
 * Bron: Frame.io-comments van de vorige versie, een mail uit je eigen mailbox, of geplakte tekst.
 * De engine ontleedt de feedback in losse punten en toetst elk punt aan transcript + keyframes.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, TextField, Typography, Box,
  Alert, CircularProgress, Chip, List, ListItemButton, ListItemText, Stack,
} from '@mui/material';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { searchMail, getConversationText, getMailBody } from '../../utils/graphMailSearch';

const API = `${AI_ENGINE_URL}/api/v1/video-search/qa`;

export default function FeedbackCheckDialog({ open, onClose, reviewId, userEmail, onKlaar }) {
  const [tab, setTab] = useState(0);
  const [frameioUrl, setFrameioUrl] = useState('');
  const [preview, setPreview] = useState(null); // {comments, bestanden}
  const [alleenOpen, setAlleenOpen] = useState(false);
  const [mailQuery, setMailQuery] = useState('');
  const [mails, setMails] = useState([]);
  const [mailGekozen, setMailGekozen] = useState(null);
  const [tekst, setTekst] = useState('');
  const [bronLabel, setBronLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const fout = (e, fallback) => setError(e?.message || fallback);

  const haalComments = async () => {
    setBusy(true); setError(null); setPreview(null);
    try {
      const r = await authFetch(`${API}/frameio-comments?url=${encodeURIComponent(frameioUrl.trim())}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'Comments ophalen mislukt');
      setPreview(d);
    } catch (e) { fout(e, 'Comments ophalen mislukt'); }
    finally { setBusy(false); }
  };

  const zoekMails = async () => {
    setBusy(true); setError(null);
    try { setMails(await searchMail(mailQuery, { top: 12 })); }
    catch (e) { fout(e, 'Mail zoeken mislukt'); }
    finally { setBusy(false); }
  };

  const kiesMail = async (m) => {
    setBusy(true); setError(null); setMailGekozen(m);
    try {
      let inhoud;
      if (m.conversationId) {
        const { tekst: t } = await getConversationText(m.conversationId, { maxChars: 12000 });
        inhoud = t;
      } else {
        const body = await getMailBody(m.id);
        inhoud = typeof body === 'string' ? body : (body?.text || body?.body || '');
      }
      setTekst(inhoud || '');
      setBronLabel(`Mail · ${m.subject}`);
      setTab(2);
    } catch (e) { fout(e, 'Mail laden mislukt'); }
    finally { setBusy(false); }
  };

  const controleer = async () => {
    setBusy(true); setError(null);
    try {
      const body = tab === 0
        ? { bron: 'frameio', frameio_url: frameioUrl.trim(), alleen_open: alleenOpen, created_by: userEmail }
        : { bron: 'tekst', tekst, bron_label: bronLabel || (tab === 1 ? 'Mail' : 'Tekst'), created_by: userEmail };
      const r = await authFetch(`${API}/reviews/${reviewId}/feedback-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'Controle mislukt');
      onKlaar?.(d);
      onClose();
    } catch (e) { fout(e, 'Controle mislukt'); }
    finally { setBusy(false); }
  };

  const kanControleren = tab === 0 ? Boolean(preview?.comments?.length) : tekst.trim().length >= 10;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>🔁 Feedback gecontroleerd?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Geef de feedback op de <b>vorige</b> versie. We splitsen die in losse punten en checken per punt
          in déze versie: verwerkt, niet verwerkt, onzeker of alleen met de hand te beoordelen. Niet-verwerkte
          punten komen als bevindingen in de lijst, dus ze lopen mee in de status en in "Push naar Frame.io".
        </Typography>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, minHeight: 36 }}>
          <Tab label="Frame.io-comments" sx={{ textTransform: 'none', minHeight: 36 }} />
          <Tab label="Mail zoeken" sx={{ textTransform: 'none', minHeight: 36 }} />
          <Tab label="Tekst plakken" sx={{ textTransform: 'none', minHeight: 36 }} />
        </Tabs>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {tab === 0 && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1}>
              <TextField fullWidth size="small" label="Frame.io-link van de vorige versie" placeholder="https://f.io/… of https://next.frame.io/share/…"
                value={frameioUrl} onChange={(e) => setFrameioUrl(e.target.value)} />
              <Button variant="outlined" onClick={haalComments} disabled={busy || !frameioUrl.trim()} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                {busy ? <CircularProgress size={16} /> : 'Comments ophalen'}
              </Button>
            </Stack>
            {preview && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Chip size="small" label={`${preview.comments.length} comments`} />
                  <Chip size="small" variant={alleenOpen ? 'filled' : 'outlined'} onClick={() => setAlleenOpen((v) => !v)}
                    label={alleenOpen ? 'alleen niet-afgevinkte' : 'alle comments'} />
                </Stack>
                <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1, p: 1 }}>
                  {preview.comments.map((c) => (
                    <Typography key={c.id} variant="body2" sx={{ mb: 0.5, opacity: alleenOpen && c.afgevinkt ? 0.4 : 1 }}>
                      {c.seconden != null && <Chip size="small" label={`${Math.floor(c.seconden / 60)}:${String(Math.floor(c.seconden % 60)).padStart(2, '0')}`} sx={{ mr: 0.5, height: 18, fontFamily: 'monospace', fontSize: 11 }} />}
                      {c.tekst}{c.door ? <span style={{ color: '#94a3b8' }}> — {c.door}</span> : null}{c.afgevinkt ? ' ✔' : ''}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1}>
              <TextField fullWidth size="small" label="Zoek in je mailbox (onderwerp, klant, projectnummer)" value={mailQuery}
                onChange={(e) => setMailQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') zoekMails(); }} />
              <Button variant="outlined" onClick={zoekMails} disabled={busy || !mailQuery.trim()} sx={{ textTransform: 'none' }}>Zoek</Button>
            </Stack>
            {mails.length > 0 && (
              <List dense sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                {mails.map((m) => (
                  <ListItemButton key={m.id} selected={mailGekozen?.id === m.id} onClick={() => kiesMail(m)}>
                    <ListItemText primary={m.subject} secondary={`${m.from} · ${m.received ? String(m.received).slice(0, 10) : ''} — ${m.preview.slice(0, 90)}`} />
                  </ListItemButton>
                ))}
              </List>
            )}
            <Typography variant="caption" color="text.secondary">Na kiezen springt hij naar "Tekst plakken" met de hele mailwisseling, zodat je nog kunt inkorten.</Typography>
          </Stack>
        )}

        {tab === 2 && (
          <TextField fullWidth multiline minRows={10} maxRows={20} value={tekst} onChange={(e) => setTekst(e.target.value)}
            label={bronLabel || 'Feedbacktekst (mail, notulen, WhatsApp…)'} placeholder="Plak hier de feedback op de vorige versie…" />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={controleer} disabled={busy || !kanControleren}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {busy ? 'Bezig… (kan een minuut duren)' : 'Controleer verwerking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
